import React from 'react';
import { matchPath } from "react-router";
import { Splitter } from "@progress/kendo-react-layout";
import {
  Component,
  Configuration,
  Dispatcher,
  Paths,
  Position,
  Router,
  PuzzleButton,
  Container,
  PuzzleToolbar,
  LocalCache, ToastNotifications as PuzzleToastNotifications
} from "puzzle";
import AnnouncementRibbon from "../Header/Announcement/Ribbon/AnnouncementRibbon"
import Footer from "../util/Footer/Footer";
import _ from 'lodash';
import Header from "../Header/Header";
import Policies from "../util/Policies";
import classNames from "classnames";
import ToastNotifications from "./ToastNotifications/ToastNotifications";
import ImpersonateUserRibbon from "../Header/Impersonate/Ribon/ImpersonateUserRibbon";
import { IMPERSONATE_CONTEXT, ImpersonateContext } from "../Header/Impersonate/Context/ImpersonateContext";
import Drawer from "../Drawer/Drawer";
import { DATA } from "../Header/Announcement/AnnouncementDialog";

type VIEWPORT_PROPS = {
  appVersion: string,
  config: {
    actions: Paths[],
    externalApps: Paths[],
    userActions: Paths[]
  },
  defaultRoute: string,
  projectLogoText: string,
  isLoggingApp: boolean
}

type VIEWPORT_STATE = {
  announcementData: any,
  panes: any[]
}

class Viewport extends Component<VIEWPORT_PROPS, VIEWPORT_STATE> {
  /**
   * Static width values for a sidebar
   */
  private SIDEBAR_WIDTH = {
    width: '48px'
  };

  constructor(props: VIEWPORT_PROPS) {
    super(props);

    this.state = {
      panes: [
        {
          size: this.SIDEBAR_WIDTH.width,
          min: this.SIDEBAR_WIDTH.width,
          resizable: false
        },
      ]
    };
  }

  moduleDidMount() {
    document.documentElement.style.setProperty('--sidebar-width', `${this.SIDEBAR_WIDTH.width}`);
    document.body.addEventListener('keydown', this.handleKeyDown, true);
  }

  componentWillUnmount() {
    Policies.reset();
    document.body.removeEventListener('keydown', this.handleKeyDown, true);
    super.componentWillUnmount();
  }

  /**
   * Returns a boolean depending on if the ribbon is shown.
   * Used for reducing server calls that are not necessary.
   */
  announcementRibbonDisplayed = () => {
    let isAnnouncementRibbonDisplayed = LocalCache.getItem('hideAnnouncement');
    return isAnnouncementRibbonDisplayed
  }

  /**
   * Focus next section on active page
   */
  focusNextSection = () => {
    let dialogElement = document.body.getElementsByClassName('k-dialog')[0];
    let elementForSearch = dialogElement ? dialogElement : document.body;
    let sections = elementForSearch.getElementsByTagName('section');
    let activeElement = document.activeElement;

    let sectionsLength = sections.length;
    let i = 0;
    let current;
    let activeSectionIndex = null;
    while (i < sectionsLength && activeSectionIndex === null) {
      current = sections[i];
      if (current.contains(activeElement)) {
        activeSectionIndex = i;
      }
      i++;
    }

    let nextSection;
    if (activeSectionIndex === null) {
      nextSection = sections[0];
    } else {
      nextSection = sections[activeSectionIndex + 1];
    }
    if (!nextSection) {
      nextSection = sections[0]
    }

    nextSection?.focus();
  }

  /**
   * Generates section for saf apps (Connect,Terminology,TM)
   */
  generateSafAppsSection = (): JSX.Element | null => {
    let safApps = [];
    const initializeUserData = _.get(Dispatcher, 'appOperationData.initializeUserData');
    const isSsoUser = !!initializeUserData && _.get(initializeUserData, 'userProfile.userInfo.isSsoUser');
    if (isSsoUser) {
      if (initializeUserData?.connectAddress) {
        safApps.push({
          icon: 'tp-icon tp-connect-navbar',
          text: this.translate('safConnect'),
          onClick: this.handleSwitchToAquaAndSaf.bind(this, 'safConnect')
        })
      }

      if (initializeUserData?.terminologyAddress) {
        safApps.push({
          icon: 'tp-icon tp-terminology-navbar',
          text: this.translate('safTerminology'),
          onClick: this.handleSwitchToAquaAndSaf.bind(this, 'safTerminology')
        })
      }

      if (initializeUserData?.tmAddress) {
        safApps.push({
          icon: 'tp-icon tp-tm-navbar',
          text: this.translate('safTM'),
          onClick: this.handleSwitchToAquaAndSaf.bind(this, 'safTM')
        })
      }
    }

    let render = null;
    if (safApps.length) {
      render = <Container
        displayFlex
        className={'tp-100 tp-toolbar-saf-app-section'}
        flexDirectionColumn
      >{safApps.map(this.getNavLinks)}
      </Container>
    }

    return render;
  };
  /**
   * returns single toolbar item configuration
   *
   * @param item
   * @param index
   */
  getNavLinks = (item: any, index: number) => {
    const path = _.isArray(item.path) ? item.path[0] : item.path;
    //items that are added in route configuration and can be redirected to but are hidden from sidebar view.
    const hiddenRoutes: any = [];
    const toolbarClassName = classNames(
      'tp-sidebar-toolbar-item tp-100 d-flex k-justify-content-center k-align-item-center',
      {
        'tp-templates-nav-item': item.path === '/templates'
      }
    )

    const navButtonClassName = classNames(
      'tp-sidebar-toolbar-item-button',
      {
        'k-disabled': item.folderDisabled,
        'tp-active': this.isActiveNavBar(item.path)
      }
    )
    return !hiddenRoutes.includes(item.path) ? (
      <Container
        className={toolbarClassName}
        key={`pd-main-sidebar-link-${index}`}
      >
        <PuzzleButton
          aria-label={item.text}
          aria-pressed={!!this.isActiveNavBar(item.path)}
          className={navButtonClassName}
          data-cy={item.text}
          titleText={item.text}
          tooltipPosition={'right'}
          iconClass={`tp-icon tp-${item.icon} tp-sidebar-toolbar-item-link-icon`}
          onClick={(event: any) => item.path ? this.navLinkOnClick(event, path) : item.onClick.call(this)}
          withTooltip
        />
      </Container>
    ) : null
  };

  /**
   * Keydown handler
   */
  handleKeyDown = (event: KeyboardEvent) => {
    const { keyCode, key, altKey, shiftKey } = event;
    let drawer = document && document.getElementById(Drawer.drawerId);
    const submissionId = drawer?.getAttribute('aria-labelledBy');

    let F4code = 115; // keycode for F4 key
    if (keyCode === F4code) {
      this.focusNextSection();
    } else if (drawer && submissionId && (altKey && shiftKey && key === 'ArrowUp')) {
      drawer?.setAttribute('data-rowId', submissionId);
      drawer?.focus();
    }
  }

  /**
   * Determines if NavBar (folder) is active currently, by matching current location.pathname with paths provided
   */
  isActiveNavBar = (path: string | string[]) => {
    const pathname = Configuration.getPathname();
    return _.isArray(path) ?
      path.some((path) => matchPath(pathname, path)) :
      matchPath(pathname, path)
  };

  /**
   * Added to support preventing route change if needed
   * @param event
   * @param path to be redirected to
   */
  navLinkOnClick = (event: React.MouseEvent, path: string) => {
    const pathname = document.location.pathname;
    if (pathname !== path) {
      //clears persistent filter from dispatcher
      this.dispatch('clearFilterMainGrid');
      //TODO need smarter solution for condition bellow
      //paths that should return true are ['/submission', '/submission/:id', '/template', '/template/:id']
      const isFromSubmissionFolder = pathname.includes('/view/submission/') || pathname.includes('/view/template/') || pathname.endsWith('/view/submission') || pathname.endsWith('/view/template');
      if (isFromSubmissionFolder) {
        event.preventDefault();
        if (path !== '/view/submission') {
          this.dispatch('checkForChangesBeforeRedirect', Position.start, { path, leaveAction: 'redirect' });
        }
      } else if (pathname.includes('/view/submissionList') && path !== '/view/submissionList') {
        event.preventDefault();
        this.dispatch('checkForDrawerChangesBeforeRedirect', Position.start, { path });
      } else if (pathname.includes('/view/preferences')) {
        this.dispatch('checkForPreferencesBeforeRedirect', {
          pos: Position.start,
          persistent: ['path']
        }, { path });
      } else {
        this.dispatch('reloadPathPD', '', { path })
      }
    }
  };

  /**
   * Handle switch to Aqua button and SAF redirects
   * @param leaveAction
   */
  handleSwitchToAquaAndSaf = (leaveAction: string = 'switchToAqua') => {
    const pathname = document.location.pathname;
    if (pathname.includes('/view/submissionList')) {
      this.dispatch('checkForDrawerChangesBeforeRedirect', '', { leaveAction });
    } else {
      this.dispatch('checkForChangesBeforeRedirect', '', { leaveAction });
    }
  };

  /**
   * Logout button render
   */
  logoutSectionRender = (isSwitch: boolean): React.ReactElement | null => {
    let logout = null;
    if (!isSwitch) {
      logout = <Container
        displayFlex
        className={'tp-100'}
        flexDirectionColumn
      >
        {[{
          onClick: () => this.dispatch('userLogout'),
          icon: 'k-icon k-i-userLogout',
          text: this.translate('logout')
        }].map(this.getNavLinks)}
      </Container>
    }

    return logout;
  };

  /**
   * Removes Announcement from local cache
   */
  removeAnnouncementCache = () => {
    LocalCache.remove(['hideAnnouncement']);
  }

  /**
   * Sets the necessary data for Announcement ribbon
   * @param announcementData
   * */
  setAnnouncementData = (announcementData: DATA[]) => {
    this.setState({ announcementData })
  }

  /**
   * Redirect to aqua ui
   */
  switchToAqua = () => {
    const { REST_SERVER } = Configuration.get();
    const { DOMAIN, PROTOCOL, PORT, NAME } = REST_SERVER;
    let aquaUrl;
    if (PORT) {
      aquaUrl = `${PROTOCOL}://${DOMAIN}:${PORT}/${NAME}`;
    } else {
      aquaUrl = `${PROTOCOL}://${DOMAIN}/${NAME}`;
    }
    window.location.href = aquaUrl;
  }

  /**
   * Renders viewport content
   */
  renderViewPortContent = (contextValue: IMPERSONATE_CONTEXT): React.ReactNode => {
    const {
      actions: actionConf,
      externalApps,
      userActions
    } = this.props.config;
    const { announcementData } = this.state
    let hideAnnouncement = LocalCache.getItem('hideAnnouncement');
    let showAnnouncementRibbon = !hideAnnouncement && !!announcementData?.length;
    const cssClass = classNames(
      'tp-viewport',
      {
        'pd-with-impersonate-user-ribbon': contextValue.isSwitch,
        'pd-with-announcement-ribbon': showAnnouncementRibbon,
      }
    );

    return (
      <div className={cssClass}>
        <header className="tp-100">
          <Header isSwitch={contextValue.isSwitch}
                  projectLogoText={this.props.projectLogoText}
                  isLoggingApp={this.props.isLoggingApp}
                  config={this.props.config}
                  appVersion={this.props.appVersion}
          />
        </header>
        {contextValue.isSwitch ? <ImpersonateUserRibbon userName={contextValue.userName} /> : null}
        {showAnnouncementRibbon &&
          <AnnouncementRibbon isImpersonatedUser={contextValue.isSwitch} announcementData={announcementData}
                              firstName={contextValue.firstName} />
        }
        <Splitter
          panes={this.state.panes}
          className="tp-viewport-content tp-no-border"
        >
          <nav className="tp-viewport-sidebar">
            <div className='tp-sidebar'>
              <PuzzleToolbar className={'tp-sidebar-toolbar tp-no-gap'}>
                <Container
                  displayFlex
                  className={'tp-100'}
                  flexDirectionColumn>
                  {actionConf.map(this.getNavLinks)}
                  {externalApps.map(this.getNavLinks)}
                </Container>
                <Container
                  displayFlex
                  className={'tp-100'}
                  flexDirectionColumn>
                  {this.generateSafAppsSection()}
                </Container>
              </PuzzleToolbar>
            </div>
          </nav>
          <main>
            <Router
              moduleName={'Navigation'}
              key={'NavigationPD'}
              config={[
                ...actionConf,
                ...userActions
              ]}
              defaultRoute={this.props.defaultRoute}
            />
          </main>
        </Splitter>
        <ToastNotifications
          moduleId={'ToastNotificationsViewport'}
        />
        <PuzzleToastNotifications
          moduleId={'ToastNotificationsPuzzle'}
        />
        <footer>
          <Footer
            openActiveSessionsDialog={true}
            impersonatedUser={contextValue.isSwitch}
          />
        </footer>
      </div>
    );
  }

  render() {
    return (
      <ImpersonateContext.Consumer>
        {(value: IMPERSONATE_CONTEXT) => this.renderViewPortContent(value)}
      </ImpersonateContext.Consumer>
    )
  }
}

export default Viewport;
